import { useEffect, useRef, useState } from "react";
import { Copy, Download, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";

// Bottom sheet on mobile (drag down, tap outside or the button to close), centred modal on
// desktop. The image is the same PNG the page publishes as its Open Graph preview.
export function ShareCardSheet({ playerId, playerName, open, onClose }) {
  const { t, lang } = useI18n();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const src = `${API}/players/${playerId}/share-card.png?lang=${lang}&layout=card`;
  const [drag, setDrag] = useState(0);
  const start = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => { if (open) setDrag(0); }, [open]);

  if (!open) return null;

  const pageUrl = window.location.href;

  const copy = async () => {
    await navigator.clipboard.writeText(pageUrl);
    toast.success(t("player.share_copied"));
  };

  const download = async () => {
    try {
      const blob = await (await fetch(src)).blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `rookie-radar-${playerId}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e?.message || "download failed");
    }
  };

  const share = async () => {
    try {
      // Share the IMAGE where the platform allows it, the link otherwise.
      const blob = await (await fetch(src)).blob();
      const file = new File([blob], `rookie-radar-${playerId}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: playerName, url: pageUrl });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: playerName, url: pageUrl });
        return;
      }
      await copy();
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(e?.message || "share failed");
    }
  };

  const onTouchStart = (e) => { start.current = e.touches[0].clientY; };
  const onTouchMove = (e) => {
    if (start.current == null) return;
    setDrag(Math.max(0, e.touches[0].clientY - start.current));
  };
  const onTouchEnd = () => {
    if (drag > 120) onClose();
    setDrag(0);
    start.current = null;
  };

  return (
    // The cookie banner sits at z-[60]: the sheet must be above it, otherwise the three
    // actions are unreachable for a first-time visitor.
    <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center" data-testid="share-card-sheet">
      <button
        aria-label={t("player.share_close")}
        onClick={onClose}
        data-testid="share-card-backdrop"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      <div
        style={{ transform: `translateY(${drag}px)`, transition: drag ? "none" : "transform .25s ease-out" }}
        className="relative w-full max-w-md rounded-t-3xl border border-[#1F293D] bg-[#0B0E14] p-4 pb-24 shadow-[0_-10px_40px_rgba(0,0,0,.6)] sm:rounded-2xl sm:pb-4"
      >
        <div
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className="mx-auto mb-3 h-1.5 w-12 cursor-grab rounded-full bg-[#1F293D] sm:hidden"
          data-testid="share-card-grabber"
        />
        <div className="mb-3 flex items-center justify-between">
          <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400">{t("player.share_title")}</p>
          <button onClick={onClose} data-testid="share-card-close" aria-label={t("player.share_close")} className="text-slate-500 hover:text-[#00FF87]">
            <X className="h-4 w-4" />
          </button>
        </div>

        <img
          src={src}
          alt={playerName}
          data-testid="share-card-preview"
          className="mx-auto max-h-[38vh] w-auto rounded-xl border border-[#1F293D] sm:max-h-[52vh]"
        />

        <div className="mt-4 grid grid-cols-3 gap-2">
          <button onClick={share} data-testid="share-card-share" className="flex flex-col items-center gap-1 rounded-lg border border-[#00FF87]/50 bg-[#00FF87]/10 py-2.5 font-mono2 text-[10px] uppercase tracking-wider text-[#00FF87] transition-colors hover:bg-[#00FF87]/20">
            <Share2 className="h-4 w-4" /> {t("player.share")}
          </button>
          <button onClick={download} data-testid="share-card-download" className="flex flex-col items-center gap-1 rounded-lg border border-[#1F293D] py-2.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-300 transition-colors hover:border-[#00F0FF]/60 hover:text-[#00F0FF]">
            <Download className="h-4 w-4" /> {t("player.share_download")}
          </button>
          <button onClick={copy} data-testid="share-card-copy" className="flex flex-col items-center gap-1 rounded-lg border border-[#1F293D] py-2.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-300 transition-colors hover:border-[#00F0FF]/60 hover:text-[#00F0FF]">
            <Copy className="h-4 w-4" /> {t("player.share_copy")}
          </button>
        </div>
      </div>
    </div>
  );
}
